import React, { useEffect, useState } from 'react';
import { Menu, X, LogIn, LogOut, LayoutDashboard, UserCircle2 } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../lib/auth';
import { scrollToSection, setPendingSection } from '../lib/sectionNavigation';

const navLinks = [
  { name: 'Services', sectionId: 'services' },
  { name: 'How It Works', sectionId: 'how-it-works' },
  { name: 'Benefits', sectionId: 'benefits' },
  { name: 'Pricing', sectionId: 'pricing' },
  { name: 'FAQ', sectionId: 'faq' },
  { name: 'Contact', sectionId: 'contact' },
];

const INVALID_DISPLAY_VALUES = new Set(['unknown', 'null', 'undefined', 'n/a', 'na', '-']);

const pickDisplayName = (user) => {
  if (!user) return '';
  const candidates = [user.email, user.preferred_username, user.username, user.name, user.sub];
  for (const value of candidates) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (!trimmed) continue;
    if (INVALID_DISPLAY_VALUES.has(trimmed.toLowerCase())) continue;
    return trimmed;
  }
  return 'User';
};

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isPastServices, setIsPastServices] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const useSolidStyle = location.pathname !== '/' || isPastServices;
  const displayName = pickDisplayName(user);

  const handleNavClick = (sectionId) => {
    if (location.pathname !== '/') {
      setPendingSection(sectionId);
      navigate('/');
      setIsMobileMenuOpen(false);
      return;
    }

    scrollToSection(sectionId);
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const updateNavbarState = () => {
      if (location.pathname !== '/') {
        setIsPastServices(true);
        return;
      }

      const services = document.getElementById('services');
      if (services) {
        const switchPoint = Math.max(services.offsetTop - 96, 24);
        setIsPastServices(window.scrollY >= switchPoint);
        return;
      }

      const hero = document.getElementById('hero');
      if (!hero) {
        setIsPastServices(window.scrollY > 24);
        return;
      }

      const switchPoint = Math.max(hero.offsetHeight - 120, 24);
      setIsPastServices(window.scrollY >= switchPoint);
    };

    updateNavbarState();
    window.addEventListener('scroll', updateNavbarState, { passive: true });
    window.addEventListener('resize', updateNavbarState);

    return () => {
      window.removeEventListener('scroll', updateNavbarState);
      window.removeEventListener('resize', updateNavbarState);
    };
  }, [location.pathname]);

  return (
    <>
      <motion.nav
        style={{ top: 'max(0.75rem, env(safe-area-inset-top, 0px))' }}
        className={`fixed left-0 right-0 z-50 mx-auto w-[92%] max-w-[1120px] rounded-xl border px-3 py-2.5 shadow-[0_8px_32px_rgba(0,0,0,0.06)] backdrop-blur-2xl transition-all duration-300 sm:w-[94%] sm:px-4 sm:py-3 md:rounded-2xl md:px-6 ${
          useSolidStyle
            ? 'border-white/40 bg-white/40'
            : 'border-white/15 bg-white/[0.04]'
        }`}
        initial={{ y: -28, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 280, damping: 30 }}
        data-testid="navbar"
      >
        <div className="flex w-full min-w-0 items-center justify-between gap-2 sm:gap-3 md:gap-4">
          <Link
            to="/"
            className={`flex w-fit shrink-0 items-center transition-all ${
              !isPastServices ? 'px-0 py-0' : ''
            }`}
            data-testid="navbar-logo"
          >
            <img
              src={isPastServices ? '/static/css/assets/img/IDTA_Logo_Blue_Web_M.png' : '/static/css/assets/img/IDTA_Logo_Grey_Web_M.png'}
              alt="IDTA"
              className={`h-8 w-auto max-w-[164px] object-contain transition-all sm:h-9 sm:max-w-[184px] ${useSolidStyle ? '' : 'brightness-0 invert'}`}
            />
          </Link>

          <nav
            className="hidden min-w-0 flex-1 justify-center px-2 lg:flex"
            aria-label="Primary"
          >
            <div className="flex max-w-full flex-wrap items-center justify-center gap-x-3 gap-y-1 lg:gap-x-5 xl:gap-x-6">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  type="button"
                  onClick={() => handleNavClick(link.sectionId)}
                  className={`border-0 bg-transparent p-0 whitespace-nowrap text-[11px] font-medium transition-colors sm:text-xs lg:text-[13px] ${
                    useSolidStyle ? 'text-[#0028CD] hover:text-[#0020a3]' : 'text-white hover:text-white/80'
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </button>
              ))}
            </div>
          </nav>

          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            {user ? (
              <div className="hidden items-center gap-1.5 sm:gap-2 lg:inline-flex">
                <Link
                  to="/dashboard"
                  className={`inline-flex cursor-pointer items-center gap-1.5 rounded-xl border px-2.5 py-2 text-xs font-bold transition-all hover:-translate-y-0.5 sm:px-3 ${
                    isPastServices
                      ? 'border-2 border-[#0028CD]/40 bg-white/80 text-[#0028CD] hover:border-[#0028CD] hover:bg-white'
                      : 'border-2 border-white/25 bg-white/[0.08] text-white hover:bg-white/[0.15]'
                  }`}
                  data-testid="nav-dashboard-btn"
                >
                  <LayoutDashboard size={14} />
                  Dashboard
                </Link>
                <div
                  className={`inline-flex max-w-[min(120px,28vw)] items-center gap-1.5 rounded-xl border-2 px-2 py-2 text-xs font-semibold sm:max-w-[140px] sm:px-3 ${
                    isPastServices
                      ? 'border-[#0028CD]/40 bg-white/80 text-[#0028CD]'
                      : 'border-white/25 bg-white/[0.08] text-white'
                  }`}
                  title={displayName}
                >
                  <UserCircle2 size={14} className="shrink-0" />
                  <span className="min-w-0 truncate">{displayName}</span>
                </div>
                <button
                  type="button"
                  onClick={() => logout()}
                  className={`inline-flex cursor-pointer items-center gap-1.5 rounded-xl border px-2.5 py-2 text-xs font-bold transition-all hover:-translate-y-0.5 sm:px-3 ${
                    isPastServices
                      ? 'border-2 border-[#0028CD]/40 bg-white/80 text-[#0028CD] hover:border-[#0028CD] hover:bg-white'
                      : 'border-2 border-white/25 bg-white/[0.08] text-white hover:bg-white/[0.15]'
                  }`}
                  data-testid="nav-logout-btn"
                  aria-label="Log out"
                >
                  <LogOut size={14} />
                  Logout
                </button>
              </div>
            ) : (
              <a
                href="/dashboard?login=1"
                className={`hidden cursor-pointer items-center gap-1.5 rounded-xl border px-5 py-2 text-xs font-bold transition-all hover:-translate-y-0.5 lg:inline-flex ${
                  useSolidStyle
                    ? 'border-2 border-[#0028CD] bg-white/80 text-[#0028CD] hover:bg-white hover:border-[#0028CD]'
                    : 'border-2 border-white/18 bg-white/[0.06] text-white hover:bg-white/[0.1] hover:border-white/22'
                } ${loading ? 'opacity-60 pointer-events-none' : ''}`}
                data-testid="nav-login-btn"
                aria-busy={loading}
              >
                <LogIn size={14} />
                Login
              </a>
            )}
            <button
              className={`border-0 bg-transparent p-1.5 transition-colors lg:hidden ${
                useSolidStyle
                  ? 'text-[#0028CD] hover:text-[#0020a3]'
                  : 'text-white hover:text-white/85'
              }`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              className="fixed inset-0 z-40 bg-[#0f172a]/20 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              aria-hidden="true"
            />
            <motion.div
              className="fixed left-0 right-0 top-20 z-50 mx-auto w-[94%] max-w-[1120px] overflow-hidden rounded-2xl border border-slate-200/60 bg-white/80 shadow-[0_8px_32px_rgba(0,0,0,0.12)] backdrop-blur-xl lg:hidden"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              data-testid="mobile-menu"
            >
              <ul className="py-4">
                {navLinks.map((link) => (
                  <li key={link.name}>
                    <button
                      type="button"
                      className="block w-full border-0 bg-transparent px-6 py-3 text-left text-[13px] font-medium text-slate-700 transition-colors hover:bg-slate-100/80 hover:text-slate-900"
                      onClick={() => handleNavClick(link.sectionId)}
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
                <li className="mt-2 border-t border-slate-200 px-4 pb-3 pt-4 space-y-2">
                  {user ? (
                    <>
                      <div className="flex items-center justify-center gap-2 rounded-xl border border-[#0028CD]/20 bg-white/60 px-4 py-2 text-xs font-semibold text-[#0028CD]">
                        <UserCircle2 size={14} />
                        <span className="truncate">{displayName}</span>
                      </div>
                      <Link
                        to="/dashboard"
                        className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-[#0028CD]/40 bg-white/80 px-5 py-2.5 text-xs font-bold text-[#0028CD] transition-all hover:border-[#0028CD] hover:bg-white"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <LayoutDashboard size={14} /> Dashboard
                      </Link>
                      <button
                        type="button"
                        onClick={() => { setIsMobileMenuOpen(false); logout(); }}
                        className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-[#0028CD]/40 bg-white/80 px-5 py-2.5 text-xs font-bold text-[#0028CD] transition-all hover:border-[#0028CD] hover:bg-white"
                      >
                        <LogOut size={14} /> Logout
                      </button>
                    </>
                  ) : (
                    <a
                      href="/dashboard?login=1"
                      className="flex items-center justify-center gap-2 rounded-xl border-2 border-[#0028CD] bg-white/80 px-5 py-2.5 text-xs font-bold text-[#0028CD] transition-all hover:bg-white hover:border-[#0028CD]"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <LogIn size={14} /> Login
                    </a>
                  )}
                </li>
              </ul>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
